import { DELIVERY_DEFAULT_CITY } from '@consts/deliveryConsts';
import type { CityType, DeliveryInfoArgs } from '@customTypes/delivery';
import { fetchCities } from '@periodica/api';
import { DELIVERY_TYPE_COURIER, DELIVERY_TYPE_PICKUP } from '@periodica/consts';
import { useQueries, useQuery } from '@tanstack/react-query';
import { getDeliveryInfo, getDeliveryWidgetData } from '../periodicaApi/deliveryCalculator';

const staleTimeForCities = 5 * 60_000; // 5 minutes
export function useCities() {
  return useQuery({
    queryKey: ['cities'],

    initialData: [DELIVERY_DEFAULT_CITY] as Awaited<ReturnType<typeof fetchCities>>,
    initialDataUpdatedAt: () => Date.now() - staleTimeForCities - 1,

    queryFn: fetchCities,
    staleTime: staleTimeForCities,
    select: (data) => {
      const mappedData = data.map((city) => ({ value: city.id, text: city.name, ...city }));

      return {
        list: mappedData,
        entities: Object.fromEntries(mappedData.map((city) => [city.id, city])),
      };
    },
  });
}

export function getSupportedDeliveryType(selectedDeliveryType: string, city: CityType) {
  if (!city.pickup) {
    return DELIVERY_TYPE_COURIER;
  }
  if (!city.courier) {
    return DELIVERY_TYPE_PICKUP;
  }

  return selectedDeliveryType;
}

export function getSupportedDeliveryProvider(
  selectedDeliveryType: string,
  availableProviders: string[]
) {
  if (!availableProviders.includes(selectedDeliveryType)) {
    return availableProviders[0];
  }

  return selectedDeliveryType;
}

type DeliveryInfoOptions = {
  product: { type: string; size: string };
  cityId: number;
  deliveryType: string;
};

type DeliveryType = typeof DELIVERY_TYPE_COURIER | typeof DELIVERY_TYPE_PICKUP;

export function useDeliveryInfoByProduct({
  product,
  cityId,
  deliveryTypes,
}: Omit<DeliveryInfoOptions, 'deliveryType'> & { deliveryTypes: DeliveryType[] }) {
  return useQueries({
    queries: deliveryTypes.map((deliveryType) => ({
      queryKey: ['product-delivery-info', product.type, product.size, cityId, deliveryType],
      staleTime: 5 * 60_000,
      queryFn: () =>
        getDeliveryWidgetData({
          cityId,
          items: [{ type: product.type, size: product.size, count: 1 }],
          isPickupDelivery: deliveryType === DELIVERY_TYPE_PICKUP,
        }),
      enabled: !!product.type,
    })),
    combine: (results) => {
      const data: Partial<Record<DeliveryType, DeliveryInfoArgs[]>> = Object.fromEntries(
        // useQueries гарантирует, что порядок results соответствует порядку queries, то есть
        // каждый айтем результата соответствует deliveryType с тем же индексом
        results.map((result, index) => [deliveryTypes[index], result.data])
      );

      return {
        data,
        isError: results.some((result) => result.isError),
        isLoading: results.some((result) => result.isLoading),
      };
    },
  });
}

export function useDeliveryInfo({ cityId, deliveryType }: Omit<DeliveryInfoOptions, 'product'>) {
  return useQuery({
    queryKey: ['delivery-info', cityId, deliveryType],
    staleTime: 5 * 60_000,
    queryFn: () =>
      getDeliveryInfo({
        cityId,
        isPickupDelivery: deliveryType === DELIVERY_TYPE_PICKUP,
      }),
    select: (data) =>
      Object.fromEntries(data.map((provider) => [provider.delivery_provider, provider])),
  });
}
